import { PACKAGE, PACKAGE_ADDONS, PACKAGE_TYPE } from 'constants/package'
import _ from 'lodash'
import moment from 'moment'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AboutUs from 'api/admin/clinic-manage/about.api'
import { ADDON_DISPLAY_CATEGORIES, ADDON_DISPLAY_LIST, ADDON_REMUNERATION_CLINIC_FEE_PDF_FOOTER, AddonDisplayItem } from 'constants/addon-display'
import { ADDON_IMPACT_BY_KEY, ADDON_GUIDE_KEY } from 'constants/addon-guide'
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import InputCheckbox from 'component/Input/InputCheckbox'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import DateInput from 'new-components/inputs/date-input'
import RadioButton from 'new-components/buttons/radio-button'
import * as useStyles from 'features/admin/clinic-management/style'
import { colors } from 'constants/theme'
import {
  getAddonDisabledList,
  getAddonSettings,
  setAddonSettings,
  getRemunerationClinicFeePdfFooter,
  setRemunerationClinicFeePdfFooter,
  getRemunerationClinicFeePdfFooterEnabled,
  getRemunerationClinicFeePdfFooterStyle,
  setRemunerationClinicFeePdfFooterStyle,
  REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT,
  RemunerationClinicFeePdfFooterStyle,
  AddonSetting,
  AddonBranchSetting
} from 'utils/app.utils'

 
export interface FormAddonProps {
  dataABout: any
}

export interface FormAddonRef {
  save: () => Promise<void>
}

function getEffectiveAddons(dataABout: any): string[] {
  const pkg = (dataABout?.package || 'STANDARD') as PACKAGE_TYPE
  const base = PACKAGE?.[pkg] ? [...PACKAGE[pkg]] : []
  let extra: string[] = []
  if (dataABout?.enableApp === '1') extra.push(PACKAGE_ADDONS.ADDON_REGISTER_APP)
  if (dataABout?.useWeb === '1') extra = [...extra]
  return [...base, ...extra]
}

function groupByCategory(items: AddonDisplayItem[]): Record<string, AddonDisplayItem[]> {
  return _.groupBy(items, 'category')
}

const CATEGORY_ORDER: (keyof typeof ADDON_DISPLAY_CATEGORIES)[] = ['BRANCH_LAYER', 'PRICE_RANGE', 'DCC', 'REQUIRE_FINANCE_ACKNOWLEDGE', 'NEW_OLD_PATIENT', 'ALERT_BEFORE_PAYMENT', 'ALERT_TEETH_BEFORE_PAYMENT', 'TREATMENT_FEE_STATEMENT', 'REPORT']

const PDF_FOOTER_COLOR_PRESETS: { value: string; labelKey: string }[] = [
  { value: '#000000', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_BLACK' },
  { value: '#333333', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_DARK_GRAY' },
  { value: '#666666', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_GRAY' },
  { value: '#cc0000', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_RED' },
  { value: '#0066cc', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_BLUE' },
  { value: '#067a52', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_GREEN' },
  { value: '#5c4033', labelKey: 'CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR_BROWN' }
]

function defaultBranchList(branchDefaultRegisterApps: any[]): AddonBranchSetting[] {
  return (branchDefaultRegisterApps || []).map((b: any) => ({
    branchId: Number(b.branchId),
    dateExpired: normalizeDateExpired(b.dateExpired),
    enabled: true
  }))
}

/** เลือก "ไม่ตั้งวันหมดอายุ" ต้องให้ dateExpired / dateExpiredAll เป็น null (ไม่ใช้ '') */
function normalizeDateExpired(v: string | null | undefined): string | null {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return null
  return v
}

/** ส่งไป API เฉพาะสาขาที่เปิดใช้ (enabled !== false) */
function normalizeAddonSettings(settings: Record<string, AddonSetting>): Record<string, AddonSetting> {
  const out: Record<string, AddonSetting> = {}
  Object.keys(settings).forEach((key) => {
    const s = settings[key]
    if (!s) return
    const branchesFiltered = (s.branches || []).filter((b) => b.enabled !== false)
    out[key] = {
      ...s,
      dateExpiredAll: normalizeDateExpired(s.dateExpiredAll),
      branches: branchesFiltered.map((b) => ({
        branchId: b.branchId,
        dateExpired: normalizeDateExpired(b.dateExpired)
      }))
    }
  })
  return out
}

function getDefaultSetting(key: string, branchDefaultRegisterApps: any[]): AddonSetting {
  return {
    enabled: false,
    useAllBranches: false,
    dateExpiredAll: null,
    branches: defaultBranchList(branchDefaultRegisterApps)
  }
}

const FormAddon = forwardRef<FormAddonRef, FormAddonProps>(function FormAddon(props, ref) {
  const { dataABout } = props
  const { t, i18n } = useTranslation()
  const branches = dataABout?.branchDefaultRegisterApps || []

  const [settings, setSettingsState] = useState<Record<string, AddonSetting>>(() => getAddonSettings())
  const [expandedAddons, setExpandedAddons] = useState<Record<string, boolean>>({})
  const [batchUseAllBranches, setBatchUseAllBranches] = useState(false)
  const [batchNoExpiry, setBatchNoExpiry] = useState(false)
  const [batchDateExpiredAll, setBatchDateExpiredAll] = useState('')
  const [batchBranches, setBatchBranches] = useState<AddonBranchSetting[]>(() =>
    defaultBranchList(dataABout?.branchDefaultRegisterApps || [])
  )
  const [selectedKeysForBatch, setSelectedKeysForBatch] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [guideAnchor, setGuideAnchor] = useState<{ el: HTMLElement; key: string } | null>(null)
  const [remunerationClinicFeePdfFooterText, setRemunerationClinicFeePdfFooterText] = useState<string>(
    () => getRemunerationClinicFeePdfFooter()
  )
  const [remunerationClinicFeePdfFooterStyle, setRemunerationClinicFeePdfFooterStyleState] =
    useState<RemunerationClinicFeePdfFooterStyle>(() => getRemunerationClinicFeePdfFooterStyle())
  const aboutUsId = dataABout?.aboutUsId != null ? Number(dataABout.aboutUsId) : null

  const grouped = useMemo(() => groupByCategory(ADDON_DISPLAY_LIST), [])
  const effectiveAddonItems = useMemo(() => ADDON_DISPLAY_LIST, [])

  /** Load feature configs from BE when aboutUsId is available */
  useEffect(() => {
    if (aboutUsId == null || !dataABout?.branchDefaultRegisterApps?.length) return
    AboutUs.getFeatureConfigs(aboutUsId)
      .then((res) => {
        const raw = res?.data || {}
        const branchIds = (dataABout.branchDefaultRegisterApps || []).map((b: any) => Number(b.branchId))
        const merged: Record<string, AddonSetting> = {}
        Object.keys(raw).forEach((key) => {
          const item = raw[key]
          const branchesMerged = branchIds.map((branchId: number) => {
            const existing = (item.branches || []).find((b: any) => Number(b.branchId) === branchId)
            return {
              branchId,
              dateExpired: normalizeDateExpired(existing?.dateExpired),
              enabled: existing != null
            }
          })
          merged[key] = {
            enabled: !!item.enabled,
            useAllBranches: !!item.useAllBranches,
            dateExpiredAll: normalizeDateExpired(item.dateExpiredAll),
            branches: branchesMerged
          }
        })
        if (Object.keys(merged).length > 0) {
          setSettingsState((prev) => ({ ...prev, ...merged }))
          setAddonSettings({ ...getAddonSettings(), ...merged })
        }
      })
      .catch(() => undefined)
  }, [aboutUsId, dataABout?.branchDefaultRegisterApps])

  useEffect(() => {
    setSettingsState(getAddonSettings())
  }, [dataABout?.package, dataABout?.enableApp, dataABout?.useWeb])

  /** Seed Addon ข้อความท้าย PDF จากค่าเดิม (REMUNERATION_CLINIC_FEE_PDF_FOOTER_ENABLED) ถ้ายังไม่มีใน settings */
  useEffect(() => {
    const current = getAddonSettings()
    if (current[ADDON_REMUNERATION_CLINIC_FEE_PDF_FOOTER]) return
    const branchList = dataABout?.branchDefaultRegisterApps || []
    const next = {
      ...current,
      [ADDON_REMUNERATION_CLINIC_FEE_PDF_FOOTER]: {
        enabled: getRemunerationClinicFeePdfFooterEnabled(),
        useAllBranches: true,
        dateExpiredAll: null,
        branches: defaultBranchList(branchList)
      }
    }
    setAddonSettings(next)
    setSettingsState(next)
  }, [dataABout?.branchDefaultRegisterApps])

  useEffect(() => {
    setBatchBranches(defaultBranchList(branches))
  }, [branches])

  const getSetting = useCallback(
    (key: string): AddonSetting => {
      const current = settings[key]
      const defaultSetting = getDefaultSetting(key, branches)
      if (!current) return defaultSetting
      const branchIds = branches.map((b: any) => Number(b.branchId))
      const branchesMerged = branchIds.map((branchId: number) => {
        const existing = _.find(current.branches, (b) => Number(b.branchId) === branchId)
        return {
          branchId,
          dateExpired: existing?.dateExpired ?? null,
          enabled: existing?.enabled !== false
        }
      })
      return {
        enabled: current.enabled,
        useAllBranches: current.useAllBranches,
        dateExpiredAll: current.dateExpiredAll,
        branches: branchesMerged
      }
    },
    [settings, branches]
  )

  const updateSetting = useCallback((key: string, patch: Partial<AddonSetting>) => {
    const branchList = dataABout?.branchDefaultRegisterApps || []
    setSettingsState((prev) => {
      const def = getDefaultSetting(key, branchList)
      const current = prev[key] || def
      const nextSetting: AddonSetting = { ...current, ...patch }
      if (patch.branches === undefined && nextSetting.branches) {
        const ids = branchList.map((b: any) => Number(b.branchId))
        nextSetting.branches = ids.map((branchId: number) => {
          const existing = _.find(current.branches, (b) => Number(b.branchId) === branchId)
          return {
            branchId,
            dateExpired: existing?.dateExpired ?? null,
            enabled: existing?.enabled !== false
          }
        })
      }
      const next = { ...prev, [key]: nextSetting }
      setAddonSettings(next)
      return next
    })
  }, [dataABout?.branchDefaultRegisterApps])

  const handleEnabledChange = useCallback(
    (key: string, checked: boolean) => {
      if (checked) {
        updateSetting(key, { enabled: true })
        setExpandedAddons((prev) => ({ ...prev, [key]: true }))
      } else {
        updateSetting(key, { enabled: false })
      }
    },
    [updateSetting]
  )

  const toggleExpanded = useCallback((key: string) => {
    setExpandedAddons((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const openGuide = useCallback((e: React.MouseEvent<HTMLElement>, addonKey: string) => {
    e.stopPropagation()
    setGuideAnchor({ el: e.currentTarget, key: addonKey })
  }, [])
  const closeGuide = useCallback(() => setGuideAnchor(null), [])
  const guideImpactKeys = guideAnchor ? ADDON_IMPACT_BY_KEY[guideAnchor.key] || [] : []

  const handleSaveFeatureConfigs = useCallback(async () => {
    if (aboutUsId == null) {
      notiError(t('CLINIC_MANAGEMENT.ERROR.UN_SUCCESS') || 'ไม่สามารถบันทึกได้')
      return
    }
    const current = getAddonSettings()
    const allowedKeys = ADDON_DISPLAY_LIST.map((item) => item.key).filter(
      (k) => k !== ADDON_REMUNERATION_CLINIC_FEE_PDF_FOOTER
    )
    const filtered = Object.fromEntries(
      Object.entries(current).filter(([key]) => allowedKeys.includes(key))
    )
    if (Object.keys(filtered).length === 0) {
      notiError(t('CLINIC_MANAGEMENT.ADDON.NO_SETTINGS_TO_SAVE') || 'ไม่มีข้อมูลการตั้งค่าให้บันทึก')
      return
    }
    setSaving(true)
    try {
      const payload = normalizeAddonSettings(filtered)
      await AboutUs.saveFeatureConfigs(aboutUsId, payload)
      notiSuccess(t('CLINIC_MANAGEMENT.ERROR.SUCCESS') || 'บันทึกสำเร็จ')
    } catch {
      notiError(t('CLINIC_MANAGEMENT.ERROR.UN_SUCCESS') || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [aboutUsId, t])

  useImperativeHandle(ref, () => ({
    save: handleSaveFeatureConfigs
  }), [handleSaveFeatureConfigs])

  const handleBatchBranchDateChange = useCallback((branchId: number, dateExpired: string | null) => {
    const value = normalizeDateExpired(dateExpired)
    setBatchBranches((prev) =>
      prev.map((b) =>
        Number(b.branchId) === branchId
          ? { ...b, dateExpired: value ? moment(value).format('YYYY-MM-DD') : null }
          : b
      )
    )
  }, [])

  const handleBatchBranchEnabledChange = useCallback((branchId: number, checked: boolean) => {
    setBatchBranches((prev) =>
      prev.map((b) =>
        Number(b.branchId) === branchId ? { ...b, enabled: checked, dateExpired: checked ? b.dateExpired : null } : b
      )
    )
  }, [])

  const handleBatchSelectAllBranches = useCallback((checked: boolean) => {
    setBatchBranches((prev) =>
      prev.map((b) => ({ ...b, enabled: checked, dateExpired: checked ? b.dateExpired : null }))
    )
  }, [])

  const handleAddonSelectAllBranches = useCallback(
    (addonKey: string, checked: boolean) => {
      const setting = getSetting(addonKey)
      const branchesUpdated = setting.branches.map((b) => ({
        ...b,
        enabled: checked,
        dateExpired: checked ? b.dateExpired : null
      }))
      updateSetting(addonKey, { branches: branchesUpdated })
    },
    [getSetting, updateSetting]
  )

  const handleSelectAllForBatch = useCallback(
    (checked: boolean) => {
      const next: Record<string, boolean> = {}
      effectiveAddonItems.forEach((item) => {
        next[item.key] = checked
      })
      setSelectedKeysForBatch(next)
    },
    [effectiveAddonItems]
  )

  const handleApplyBatchToSelected = useCallback(() => {
    const keysToApply = effectiveAddonItems.filter((item) => selectedKeysForBatch[item.key]).map((item) => item.key)
    if (keysToApply.length === 0) return
    const template: Partial<AddonSetting> = {
      enabled: true,
      useAllBranches: batchUseAllBranches,
      dateExpiredAll: batchUseAllBranches ? (batchNoExpiry ? null : batchDateExpiredAll || null) : null,
      branches: batchUseAllBranches ? defaultBranchList(branches) : _.cloneDeep(batchBranches)
    }
    setSettingsState((prev) => {
      const next = { ...prev }
      keysToApply.forEach((key) => {
        next[key] = {
          ...getDefaultSetting(key, branches),
          ...next[key],
          ...template,
          branches: template.branches ?? next[key]?.branches ?? defaultBranchList(branches)
        }
      })
      setAddonSettings(next)
      return next
    })
  }, [
    effectiveAddonItems,
    selectedKeysForBatch,
    batchUseAllBranches,
    batchNoExpiry,
    batchDateExpiredAll,
    batchBranches,
    branches
  ])

  const handleUseAllBranchesChange = useCallback(
    (key: string, value: string) => {
      updateSetting(key, { useAllBranches: value === '1' })
    },
    [updateSetting]
  )

  const handleDateExpiredAllChange = useCallback(
    (key: string, date: string | null) => {
      updateSetting(key, { dateExpiredAll: date ? moment(date).format('YYYY-MM-DD') : null })
    },
    [updateSetting]
  )

  const handleBranchDateChange = useCallback(
    (key: string, branchId: number, dateExpired: string | null) => {
      const value = normalizeDateExpired(dateExpired)
      const setting = getSetting(key)
      const branchesUpdated = setting.branches.map((b) =>
        Number(b.branchId) === branchId ? { ...b, dateExpired: value ? moment(value).format('YYYY-MM-DD') : null } : b
      )
      updateSetting(key, { branches: branchesUpdated })
    },
    [getSetting, updateSetting]
  )

  const handleBranchEnabledChange = useCallback(
    (key: string, branchId: number, checked: boolean) => {
      const setting = getSetting(key)
      const branchesUpdated = setting.branches.map((b) =>
        Number(b.branchId) === branchId
          ? { ...b, enabled: checked, dateExpired: checked ? b.dateExpired : null }
          : b
      )
      updateSetting(key, { branches: branchesUpdated })
    },
    [getSetting, updateSetting]
  )

  const getAddonSummary = useCallback(
    (setting: AddonSetting) => {
      if (!setting.enabled) return ''
      if (setting.useAllBranches) {
        return setting.dateExpiredAll
          ? t('CLINIC_MANAGEMENT.ADDON.SUMMARY_ALL', { date: moment(setting.dateExpiredAll).format('DD/MM/YYYY') })
          : t('CLINIC_MANAGEMENT.ADDON.SUMMARY_NO_EXPIRY')
      }
      const count = setting.branches.filter((b) => b.enabled !== false).length
      return t('CLINIC_MANAGEMENT.ADDON.SUMMARY_PER_BRANCH', { count, total: setting.branches.length })
    },
    [t]
  )

  const renderBranchRows = (addonKey: string) => {
    const setting = getSetting(addonKey)
    return branches.map((objData: any, index: number) => {
      const branchId = Number(objData.branchId)
      const branchEntry = _.find(setting.branches, (b) => Number(b.branchId) === branchId)
      const dateExpired = branchEntry?.dateExpired ?? ''
      const enabledAtBranch = branchEntry?.enabled !== false
      return (
        <TableRow key={`${addonKey}-${branchId}`}>
          <TableCell align="center" sx={{ width: 60 }}>{index + 1}</TableCell>
          <TableCell sx={{ minWidth: 280 }}>{objData.branchName ?? objData.branchNameEn}</TableCell>
          <TableCell sx={{ width: 160 }}>
            <InputCheckbox
              checked={enabledAtBranch}
              onChange={(e) => handleBranchEnabledChange(addonKey, branchId, e.target.checked)}
            />
          </TableCell>
          <TableCell>
            <DateInput
              label={t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}
              onChange={(e) => handleBranchDateChange(addonKey, branchId, e ? moment(e).format('YYYY-MM-DD') : null)}
              value={dateExpired}
              width={280}
              onClear={(e) => {
                e.stopPropagation()
                handleBranchDateChange(addonKey, branchId, null)
              }}
              disabled={!enabledAtBranch}
              disabledDatePast
              language={i18n.language.toUpperCase() as any}
            />
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <Box className="px-4 py-2">
      <useStyles.TitleFirst>
        <p className="d-flex align-items-center mb-2">{t('CLINIC_MANAGEMENT.ADDON.TITLE')}</p>
      </useStyles.TitleFirst>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('CLINIC_MANAGEMENT.ADDON.DESCRIPTION')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {t('CLINIC_MANAGEMENT.ADDON.HINT_DEFAULT_DATE')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('CLINIC_MANAGEMENT.ADDON.CURRENT_PACKAGE')}:{' '}
          <Typography component="span" fontWeight={600} color="text.primary">
            {dataABout?.package || 'STANDARD'}
          </Typography>
        </Typography>
      </Box>

      <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: colors.themeSecondColor }}>
          {t('CLINIC_MANAGEMENT.ADDON.BATCH_TITLE')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {t('CLINIC_MANAGEMENT.ADDON.BATCH_DESCRIPTION')}
        </Typography>
        <RadioButton
          name="batch-scope"
          value={batchUseAllBranches ? '1' : '0'}
          onChange={(e) => setBatchUseAllBranches(e.target.value === '1')}
          options={[
            { label: t('CLINIC_MANAGEMENT.ADDON.USE_PER_BRANCH'), value: '0' },
            { label: t('CLINIC_MANAGEMENT.ADDON.USE_ALL_BRANCHES'), value: '1' }
          ]}
          sx={{ gap: 1, mb: 1 }}
        />
        {batchUseAllBranches ? (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Box sx={{ mb: 1 }}>
              <InputCheckbox
                checked={batchNoExpiry}
                onChange={(e) => {
                  setBatchNoExpiry(e.target.checked)
                  if (e.target.checked) setBatchDateExpiredAll('')
                }}
                label={t('CLINIC_MANAGEMENT.ADDON.NO_EXPIRY_DATE')}
              />
            </Box>
            {!batchNoExpiry && (
              <DateInput
                label={t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}
                value={batchDateExpiredAll}
                onChange={(e) => setBatchDateExpiredAll(e ? moment(e).format('YYYY-MM-DD') : '')}
                onClear={() => setBatchDateExpiredAll('')}
                width={300}
                disabledDatePast
                language={i18n.language.toUpperCase() as any}
              />
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 1, mb: 2, maxWidth: 720, overflow: 'auto' }}>
            <Table size="small" sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 60 }}>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.INDEX')}</TableCell>
                  <TableCell sx={{ width: 200 }}>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.BRANCH')}</TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <InputCheckbox
                      checked={batchBranches.length > 0 && batchBranches.every((b) => b.enabled !== false)}
                      indeterminate={
                        batchBranches.some((b) => b.enabled !== false) &&
                        !batchBranches.every((b) => b.enabled !== false)
                      }
                      onChange={(e) => handleBatchSelectAllBranches(e.target.checked)}
                      label={t('CLINIC_MANAGEMENT.ADDON.ENABLED_AT_BRANCH')}
                    />
                  </TableCell>
                  <TableCell>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((objData: any, index: number) => {
                  const branchId = Number(objData.branchId)
                  const branchEntry = _.find(batchBranches, (b) => Number(b.branchId) === branchId)
                  const dateExpired = branchEntry?.dateExpired ?? ''
                  const enabledAtBranch = branchEntry?.enabled !== false
                  return (
                    <TableRow key={`batch-${branchId}`}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell>{objData.branchName ?? objData.branchNameEn}</TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <InputCheckbox
                          checked={enabledAtBranch}
                          onChange={(e) => handleBatchBranchEnabledChange(branchId, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <DateInput
                          label={t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}
                          onChange={(e) => handleBatchBranchDateChange(branchId, e ? moment(e).format('YYYY-MM-DD') : null)}
                          value={dateExpired}
                          width={280}
                          onClear={(e) => {
                            e.stopPropagation()
                            handleBatchBranchDateChange(branchId, null)
                          }}
                          disabled={!enabledAtBranch}
                          disabledDatePast
                          language={i18n.language.toUpperCase() as any}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {t('CLINIC_MANAGEMENT.ADDON.BATCH_APPLY_TO')}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ mb: 1.5 }}>
            <InputCheckbox
              checked={effectiveAddonItems.length > 0 && effectiveAddonItems.every((item) => selectedKeysForBatch[item.key])}
              indeterminate={
                effectiveAddonItems.length > 0 &&
                effectiveAddonItems.some((item) => selectedKeysForBatch[item.key]) &&
                !effectiveAddonItems.every((item) => selectedKeysForBatch[item.key])
              }
              onChange={(e) => handleSelectAllForBatch(e.target.checked)}
              label={t('CLINIC_MANAGEMENT.ADDON.BATCH_SELECT_ALL')}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
              pl: 0.5
            }}
          >
            {CATEGORY_ORDER.map((cat) => {
              const list = grouped[cat]
              if (!list?.length) return null
              return (
                <Box key={cat} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t(ADDON_DISPLAY_CATEGORIES[cat])}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {list.map((item) => (
                      <InputCheckbox
                        key={item.key}
                        checked={!!selectedKeysForBatch[item.key]}
                        onChange={(e) =>
                          setSelectedKeysForBatch((prev) => ({ ...prev, [item.key]: e.target.checked }))
                        }
                        label={t(item.labelKey)}
                      />
                    ))}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleApplyBatchToSelected}
          disabled={
            !effectiveAddonItems.some((item) => selectedKeysForBatch[item.key]) ||
            (batchUseAllBranches && !batchNoExpiry && !batchDateExpiredAll)
          }
          sx={{ bgcolor: colors.themeSecondColor, '&:hover': { bgcolor: colors.themeSecondColor, opacity: 0.9 } }}
        >
          {t('CLINIC_MANAGEMENT.ADDON.BATCH_APPLY_BTN')}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {CATEGORY_ORDER.map((cat) => {
        const list = grouped[cat]
        if (!list?.length) return null
        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ mb: 1.5, color: colors.themeSecondColor }}
            >
              {t(ADDON_DISPLAY_CATEGORIES[cat])}
            </Typography>
            <Box sx={{ marginLeft: '10px' }}>
              {list.map((item) => {
                const setting = getSetting(item.key)
                const summary = getAddonSummary(setting)
                const isExpanded = !!expandedAddons[item.key]

                return (
                  <Box key={item.key} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <InputCheckbox
                        checked={setting.enabled}
                        onChange={(e) => handleEnabledChange(item.key, e.target.checked)}
                        label={t(item.labelKey)}
                      />
                      {ADDON_IMPACT_BY_KEY[item.key]?.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={(e) => openGuide(e, item.key)}
                          sx={{ color: 'text.secondary', p: 0.25 }}
                          aria-label={t(`${ADDON_GUIDE_KEY}.TITLE`)}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      )}
                      {setting.enabled && summary && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {summary}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(item.key)}
                            aria-label={isExpanded ? 'ย่อ' : 'ขยาย'}
                            sx={{ p: 0.25, ml: 0.5 }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </>
                      )}
                    </Box>
                    {setting.enabled && (
                      <Collapse in={isExpanded}>
                        <Box sx={{ pl: 3, pt: 1, pb: 1, borderLeft: '2px solid', borderColor: 'divider', ml: 1.5 }}>
                          <RadioButton
                            name={`addon-scope-${item.key}`}
                            value={setting.useAllBranches ? '1' : '0'}
                            onChange={(e) => handleUseAllBranchesChange(item.key, e.target.value)}
                            options={[
                              { label: t('CLINIC_MANAGEMENT.ADDON.USE_PER_BRANCH'), value: '0' },
                              { label: t('CLINIC_MANAGEMENT.ADDON.USE_ALL_BRANCHES'), value: '1' }
                            ]}
                            sx={{ gap: 1, mb: 1 }}
                          />
                          {setting.useAllBranches ? (
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ mb: 1 }}>
                                <InputCheckbox
                                  checked={!setting.dateExpiredAll}
                                  onChange={(e) => {
                                    if (e.target.checked) handleDateExpiredAllChange(item.key, null)
                                  }}
                                  label={t('CLINIC_MANAGEMENT.ADDON.NO_EXPIRY_DATE')}
                                />
                              </Box>
                              <DateInput
                                label={t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}
                                value={setting.dateExpiredAll || ''}
                                onChange={(e) =>
                                  handleDateExpiredAllChange(
                                    item.key,
                                    e ? moment(e).format('YYYY-MM-DD') : null
                                  )
                                }
                                onClear={(e) => {
                                  e.stopPropagation()
                                  handleDateExpiredAllChange(item.key, null)
                                }}
                                width={300}
                                disabledDatePast
                                language={i18n.language.toUpperCase() as any}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ mt: 1, maxWidth: 960, overflow: 'auto' }}>
                              <Table size="small" sx={{ minWidth: 640 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell align="center" sx={{ width: 60 }}>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.INDEX')}</TableCell>
                                    <TableCell sx={{ minWidth: 280 }}>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.BRANCH')}</TableCell>
                                    <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                                      {(() => {
                                        const setting = getSetting(item.key)
                                        const allEnabled = setting.branches.every((b) => b.enabled !== false)
                                        const someEnabled = setting.branches.some((b) => b.enabled !== false)
                                        return (
                                          <InputCheckbox
                                            checked={allEnabled}
                                            indeterminate={someEnabled && !allEnabled}
                                            onChange={(e) => handleAddonSelectAllBranches(item.key, e.target.checked)}
                                            label={t('CLINIC_MANAGEMENT.ADDON.ENABLED_AT_BRANCH')}
                                          />
                                        )
                                      })()}
                                    </TableCell>
                                    <TableCell>{t('CLINIC_MANAGEMENT.SELECT_RADIO.TABLE.DATE_EXPIRED')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>{renderBranchRows(item.key)}</TableBody>
                              </Table>
                            </Box>
                          )}
                          {item.key === ADDON_REMUNERATION_CLINIC_FEE_PDF_FOOTER && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {t('CLINIC_MANAGEMENT.ADDON.REMUNERATION_CLINIC_FEE_PDF_FOOTER_DESC', 'ข้อความนี้จะแสดงที่ท้ายเอกสาร PDF เมื่อพิมพ์รายงานค่าตอบแทนทันตแพทย์หัก Clinic Fee')}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_SIZE', 'ขนาดตัวอักษร')}</InputLabel>
                                  <Select
                                    value={remunerationClinicFeePdfFooterStyle.fontSize ?? 10}
                                    label={t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_SIZE', 'ขนาดตัวอักษร')}
                                    onChange={(e) => {
                                      const next = { ...remunerationClinicFeePdfFooterStyle, fontSize: Number(e.target.value) }
                                      setRemunerationClinicFeePdfFooterStyleState(next)
                                      setRemunerationClinicFeePdfFooterStyle(next)
                                    }}
                                  >
                                    {[8, 9, 10, 11, 12].map((n) => (
                                      <MenuItem key={n} value={n}>{n} px</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_WEIGHT', 'ความหนา')}</InputLabel>
                                  <Select
                                    value={remunerationClinicFeePdfFooterStyle.fontWeight ?? 500}
                                    label={t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_WEIGHT', 'ความหนา')}
                                    onChange={(e) => {
                                      const next = { ...remunerationClinicFeePdfFooterStyle, fontWeight: Number(e.target.value) }
                                      setRemunerationClinicFeePdfFooterStyleState(next)
                                      setRemunerationClinicFeePdfFooterStyle(next)
                                    }}
                                  >
                                    <MenuItem value={400}>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_NORMAL', 'ปกติ')}</MenuItem>
                                    <MenuItem value={500}>500</MenuItem>
                                    <MenuItem value={600}>600</MenuItem>
                                    <MenuItem value={700}>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_BOLD', 'ตัวหนา')}</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_ALIGN', 'จัดแนว')}</InputLabel>
                                  <Select
                                    value={remunerationClinicFeePdfFooterStyle.textAlign ?? 'right'}
                                    label={t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_ALIGN', 'จัดแนว')}
                                    onChange={(e) => {
                                      const next = { ...remunerationClinicFeePdfFooterStyle, textAlign: e.target.value as 'left' | 'center' | 'right' }
                                      setRemunerationClinicFeePdfFooterStyleState(next)
                                      setRemunerationClinicFeePdfFooterStyle(next)
                                    }}
                                  >
                                    <MenuItem value="left">{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_LEFT', 'ซ้าย')}</MenuItem>
                                    <MenuItem value="center">{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_CENTER', 'กลาง')}</MenuItem>
                                    <MenuItem value="right">{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_RIGHT', 'ขวา')}</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_STYLE', 'รูปแบบ')}</InputLabel>
                                  <Select
                                    value={remunerationClinicFeePdfFooterStyle.fontStyle ?? 'normal'}
                                    label={t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_FONT_STYLE', 'รูปแบบ')}
                                    onChange={(e) => {
                                      const next = { ...remunerationClinicFeePdfFooterStyle, fontStyle: e.target.value as 'normal' | 'italic' }
                                      setRemunerationClinicFeePdfFooterStyleState(next)
                                      setRemunerationClinicFeePdfFooterStyle(next)
                                    }}
                                  >
                                    <MenuItem value="normal">{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_NORMAL', 'ปกติ')}</MenuItem>
                                    <MenuItem value="italic">{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_ITALIC', 'ตัวเอียง')}</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>{t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR', 'สีตัวอักษร')}</InputLabel>
                                  <Select
                                    value={
                                      PDF_FOOTER_COLOR_PRESETS.some((p) => p.value === (remunerationClinicFeePdfFooterStyle.color ?? ''))
                                        ? (remunerationClinicFeePdfFooterStyle.color ?? '#000000')
                                        : '#000000'
                                    }
                                    label={t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_STYLE_COLOR', 'สีตัวอักษร')}
                                    onChange={(e) => {
                                      const next = { ...remunerationClinicFeePdfFooterStyle, color: e.target.value }
                                      setRemunerationClinicFeePdfFooterStyleState(next)
                                      setRemunerationClinicFeePdfFooterStyle(next)
                                    }}
                                  >
                                    {PDF_FOOTER_COLOR_PRESETS.map((preset) => (
                                      <MenuItem key={preset.value} value={preset.value}>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: preset.value, border: '1px solid', borderColor: 'divider' }} />
                                          {t(preset.labelKey)}
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={6}
                                value={remunerationClinicFeePdfFooterText}
                                onChange={(e) => setRemunerationClinicFeePdfFooterText(e.target.value)}
                                onBlur={() => setRemunerationClinicFeePdfFooter(remunerationClinicFeePdfFooterText)}
                                placeholder={REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT}
                                size="small"
                                sx={{ maxWidth: 720, mb: 1 }}
                              />
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setRemunerationClinicFeePdfFooterText(REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT)
                                  setRemunerationClinicFeePdfFooter(REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT)
                                }}
                              >
                                {t('CLINIC_MANAGEMENT.ADDON.RESET_TO_DEFAULT', 'ใช้ข้อความต้นฉบับ')}
                              </Button>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, mb: 0.5 }}>
                                {t('CLINIC_MANAGEMENT.ADDON.PDF_FOOTER_PREVIEW', 'ตัวอย่างการแสดงผล')}
                              </Typography>
                              <Box
                                sx={{
                                  maxWidth: 720,
                                  p: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  backgroundColor: 'grey.50'
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  sx={{
                                    fontSize: `${remunerationClinicFeePdfFooterStyle.fontSize ?? 10}px`,
                                    fontWeight: remunerationClinicFeePdfFooterStyle.fontWeight ?? 500,
                                    fontStyle: remunerationClinicFeePdfFooterStyle.fontStyle ?? 'normal',
                                    textAlign: remunerationClinicFeePdfFooterStyle.textAlign ?? 'right',
                                    color: remunerationClinicFeePdfFooterStyle.color ?? '#000000'
                                  }}
                                >
                                  {remunerationClinicFeePdfFooterText.trim() !== ''
                                    ? remunerationClinicFeePdfFooterText
                                    : REMUNERATION_CLINIC_FEE_PDF_FOOTER_DEFAULT}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}

      <Popover
        open={!!guideAnchor}
        anchorEl={guideAnchor?.el}
        onClose={closeGuide}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxWidth: 400, mt: 0.5 } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t(`${ADDON_GUIDE_KEY}.TITLE`)}
          </Typography>
          <List dense disablePadding>
            {guideImpactKeys.map((localeKey, idx) => (
              <ListItem key={idx} disablePadding sx={{ alignItems: 'flex-start', py: 0.25 }}>
                <ListItemText
                  primary={t(localeKey)}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  )
})

export default FormAddon
